import {
  Form,
  Divider,
  Row,
  Col,
  Button,
  Upload,
  UploadFile,
  Checkbox,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { ImportModalFormProps } from '..';
import {
  useWorkorderImportI360,
  useWorkorderImportSoma,
} from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.hooks';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
} from '@/components/_common';
import useDefaultEmptyFieldError from '@/hooks/useDefaultEmptyFieldError';
import { RcFile } from 'antd/lib/upload';
import { ImportComplementOrigin } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.enum';
import HelpIcon from '@/components/_common/HelpIcon/idex';

type Props = ImportModalFormProps;
type FormValues = {
  file: UploadFile[];
  companyId: number;
  onlyTypedParts?: boolean;
};

export const typeOption = [
  'audatex',
  'sOMA',
  'cilia',
  'audatexWeb',
  'i360',
  'localiza',
  'json',
];

const ImportFileForm: React.FC<Props> = (props) => {
  const [form] = Form.useForm<FormValues>();
  const requiredRule = useDefaultEmptyFieldError();

  const importSoma = useWorkorderImportSoma();
  const importI360 = useWorkorderImportI360();

  const isLoading = importSoma.isLoading || importI360.isLoading;

  function convertFileToBase64(file: RcFile) {
    return new Promise<string>((res, rej) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        res(
          (reader.result as string)?.replace('data:', '')?.replace(/^.+,/, ''),
        );
      };
      reader.onerror = (error) => rej(error);
      reader.readAsDataURL(file);
    });
  }

  async function handleSubmit(values: FormValues) {
    if (!!values.file && values.file[0].originFileObj) {
      let result;

      const workorderFile = await convertFileToBase64(
        values.file[0].originFileObj,
      );

      if (props.origin === ImportComplementOrigin.Soma) {
        result = await importSoma.mutateAsync({
          companyId: values.companyId,
          budgetSoma: workorderFile,
          onlyTypedParts: values.onlyTypedParts,
        });
      } else if (props.origin === ImportComplementOrigin.I360) {
        result = await importI360.mutateAsync({
          companyId: values.companyId,
          xmli360: workorderFile,
        });
      }

      if (result) {
        props.onImported({
          data: result,
          origin: props.origin,
        });
      }
    }
  }

  function handleClose() {
    form.resetFields();
    props.onClose();
  }

  return (
    <Form name="import-file" form={form} onFinish={handleSubmit}>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <FloatingFormItem
            name="companyId"
            label="Empresa"
            rules={[requiredRule]}
          >
            <CompaniesInfiniteSelectField />
          </FloatingFormItem>
        </Col>

        {props.origin === ImportComplementOrigin.Soma && (
          <Col span={24}>
            <HelpIcon title="Ao ativar esta opção, todas as peças não fornecidas serão tratadas como peças digitadas.">
              <Form.Item
                noStyle
                name="onlyTypedParts"
                initialValue={false}
                valuePropName="checked"
              >
                <Checkbox>Somente peças digitadas</Checkbox>
              </Form.Item>
            </HelpIcon>
          </Col>
        )}

        <Col span={24}>
          <Form.Item
            name="file"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList;
            }}
          >
            <Upload
              multiple={false}
              customRequest={({ file, onError, onSuccess }) => {
                try {
                  !!onSuccess && onSuccess([file]);
                } catch (error: unknown) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  !!onError && onError(error as any);
                }
              }}
              maxCount={1}
              type="drag"
            >
              <Group gap={10} position="center">
                <UploadOutlined style={{ fontSize: 16 }} />
                Importar arquivo
              </Group>
            </Upload>
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Row justify="end" gutter={[16, 16]}>
        <Col>
          <Button onClick={handleClose}>Cancelar</Button>
        </Col>
        <Col>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Importar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ImportFileForm;
